import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X, CheckCircle } from "@phosphor-icons/react";

const TYPES = ["preventive", "corrective"];
const STATUSES = ["open", "in_progress", "completed", "cancelled"];

export default function AssetMaintenancePage() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    asset_id: "", work_order_no: "", maintenance_type: "preventive",
    description: "", labor_cost: "0", material_cost: "0",
    service_provider: "", scheduled_date: "", completed_date: "",
    status: "open", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/alm/maintenance"), api.get("/alm/assets")]);
    setRows(a.data); setAssets(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/maintenance", {
      ...form,
      labor_cost: parseFloat(form.labor_cost || 0),
      material_cost: parseFloat(form.material_cost || 0),
    });
    setOpen(false);
    setForm({ ...form, asset_id: "", work_order_no: "", description: "", labor_cost: "0", material_cost: "0", service_provider: "", scheduled_date: "", completed_date: "", notes: "" });
    load();
  };

  const complete = async (id) => { await api.post(`/alm/maintenance/${id}/complete`); load(); };
  const remove = async (id) => { if (window.confirm("Delete this work order?")) { await api.delete(`/alm/maintenance/${id}`); load(); } };

  const statusBadge = (s) => {
    const map = {
      open: "bg-yellow-100 text-yellow-800",
      in_progress: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-gray-200 text-gray-700",
    };
    return <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 ${map[s] || "bg-gray-100"}`}>{(s || "").replace("_", " ")}</span>;
  };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Asset Maintenance"
        subtitle="Plan preventive or corrective maintenance work orders and track labor & material costs."
        testid="alm-maint-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-maint-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> New Work Order
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-maint-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["WO #", "Asset", "Type", "Provider", "Scheduled", "Status", "Total Cost", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.work_order_no}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.asset_tag}</td>
                  <td className="px-4 py-3 text-xs uppercase">{r.maintenance_type}</td>
                  <td className="px-4 py-3 text-xs">{r.service_provider}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.scheduled_date)}</td>
                  <td className="px-4 py-3">{statusBadge(r.status)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.total_cost)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status !== "completed" && r.status !== "cancelled" && (
                      <button onClick={() => complete(r.id)} data-testid={`alm-maint-complete-${r.id}`} className="text-green-700 hover:underline text-xs font-bold inline-flex items-center gap-1 mr-3">
                        <CheckCircle size={12} weight="bold" /> Complete
                      </button>
                    )}
                    <button onClick={() => remove(r.id)} data-testid={`alm-maint-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">No work orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-maint-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Work Order</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Asset *</label>
                <select required value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })} data-testid="alm-maint-asset" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Asset —</option>
                  {assets.filter(a => a.status !== "retired").map(a => <option key={a.id} value={a.id}>{a.asset_tag} — {a.product_code}</option>)}
                </select>
              </div>
              {[
                ["work_order_no", "Work Order # *", "text", true],
                ["description", "Description", "text", false],
                ["service_provider", "Service Provider", "text", false],
                ["scheduled_date", "Scheduled Date", "date", false],
                ["labor_cost", "Labor Cost", "number", false],
                ["material_cost", "Material Cost", "number", false],
                ["notes", "Notes", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-maint-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Type</label>
                  <select value={form.maintenance_type} onChange={e => setForm({ ...form, maintenance_type: e.target.value })} data-testid="alm-maint-type" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="alm-maint-status" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-maint-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create Work Order</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
